import { type Atom, combine, compute, dedupe } from '@exodus/atoms';
import type { Definition } from '@exodus/dependency-types';

import type { Assets } from '@/types';

const createAssetCannotBeDisabledReasonAtom = ({
  assetsModule,
  defaultEnabledAssetNamesAtom,
  nonDustBalanceAssetNamesAtom,
  availableAssetNamesAtom,
  enabledAndDisabledAssetsAtom,
}) => {
  const selector = ({
    availableAssetNames,
    defaultEnabledAssetNames,
    nonDustBalanceAssetNames,
    enabledAndDisabledAssets,
  }) => {
    const assets: Assets = assetsModule.getAssets();
    const reasons = Object.fromEntries(
      availableAssetNames
        .map((assetName: string) => {
          if (enabledAndDisabledAssets.disabled[assetName] !== false) {
            return undefined;
          }

          if (defaultEnabledAssetNames.includes(assetName)) {
            return [assetName, 'ENABLED_BY_DEFAULT'];
          }

          if (nonDustBalanceAssetNames.includes(assetName)) {
            return [assetName, 'IT_HAS_BALANCE'];
          }

          const asset = assets[assetName];
          const tokenWithBalance =
            asset.name === asset.baseAsset.name &&
            Object.values(assets).find(
              (token) =>
                token.baseAsset.name === assetName &&
                nonDustBalanceAssetNames.includes(token.name),
            );

          if (tokenWithBalance) {
            return [assetName, 'IT_HAS_TOKEN_WITH_BALANCE'];
          }
          // Include enabled assets only.
          return undefined;
        })
        .filter(Boolean),
    );

    return reasons;
  };

  return dedupe(
    compute({
      atom: combine({
        availableAssetNames: availableAssetNamesAtom,
        defaultEnabledAssetNames: defaultEnabledAssetNamesAtom,
        nonDustBalanceAssetNames: nonDustBalanceAssetNamesAtom,
        enabledAndDisabledAssets: enabledAndDisabledAssetsAtom,
      }),
      selector,
    }) as Atom<unknown>,
  );
};

export const assetCannotBeDisabledReasonAtom = {
  id: 'assetCannotBeDisabledReasonAtom',
  type: 'atom',
  factory: createAssetCannotBeDisabledReasonAtom,
  dependencies: [
    'assetsModule',
    'defaultEnabledAssetNamesAtom',
    'nonDustBalanceAssetNamesAtom',
    'availableAssetNamesAtom',
    'enabledAndDisabledAssetsAtom',
  ],
} as const satisfies Definition;
